import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  Button,
  Popconfirm,
  Form,
  Select,
  Collapse,
  InputNumber,
  DatePicker,
  Dropdown,
} from "antd";
import { CalendarOutlined, CaretRightOutlined } from "@ant-design/icons";
import "antd/dist/antd.css";
import moment from "moment";

const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const EditableContext = React.createContext();
const TableContext = React.createContext();

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  const tableCtx = useContext(TableContext);
  const rowKey = props["data-row-key"];

  useEffect(() => {
    if (tableCtx && tableCtx.registerForm && rowKey) {
      tableCtx.registerForm(rowKey, form);
      return () => {
        tableCtx.unregisterForm(rowKey);
      };
    }
  }, [tableCtx, rowKey, form]);

  return (
    <>
      <Form
        form={form}
        component={false}
        onFinish={(values) => {
          console.log("current", values);
        }}
      >
        <EditableContext.Provider value={form} className={"rowForm"}>
          <tr {...props}></tr>
          <tr style={{ display: "none" }}>
            <td>
              <Button
                onClick={form.submit}
                className={"submitAllForm"}
              ></Button>
            </td>
          </tr>
        </EditableContext.Provider>
      </Form>
    </>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  handling,
  isCreate,
  dataIndex,
  record,
  handleSave,
  required,
  option,
  type,
  validate,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef();
  const form = useContext(EditableContext);
  const tableCtx = useContext(TableContext);
  const [openDate, setOpenDate] = useState(false);
  const [valueDate, setValueDate] = useState(undefined);
  const [openRangeDate, setOpenRangeDate] = useState(false);
  const [valueRangeDate, setValueRangeDate] = useState(undefined);
  const isShiftPressed = useRef(false);

  const handleKeyDown = (e) => {
    if (
      e.ctrlKey &&
      e.shiftKey &&
      (e.key === "v" || e.key === "V" || e.keyCode === 86)
    ) {
      isShiftPressed.current = true;
      setTimeout(() => {
        isShiftPressed.current = false;
      }, 300);
    }
  };

  const handlePaste = (e) => {
    if (isShiftPressed.current) {
      e.preventDefault();
      e.stopPropagation();
      const text =
        e.clipboardData.getData("text/plain") ||
        e.clipboardData.getData("text");
      if (tableCtx && tableCtx.handlePaste) {
        tableCtx.handlePaste(text, dataIndex, record);
      }
      isShiftPressed.current = false;
    }
  };

  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(true);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async (e, dataIndex) => {
    try {
      let value =
        typeof e === "string"
          ? e
          : form && form.getFieldValue(dataIndex) !== undefined
            ? form.getFieldValue(dataIndex)
            : e.target && e.target.value !== undefined
              ? e.target.value
              : e.target.defaultValue;

      if (dataIndex.includes("Số tiền") || typeof value === "number") {
        value = value
          .toString()
          .replace(/,/g, "")
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        form.setFieldsValue({
          [dataIndex]: value,
        });
      }

      handleSave({ ...record }, value, dataIndex);
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  const handleDateChange = (date, dateString) => {
    save(dateString, dataIndex);
    setOpenDate(false);
    setValueDate(date);
    form.setFieldsValue({
      [dataIndex]: dateString,
    });
  };

  const checkDateUpdate = (data) => {
    const spFormats = ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "DD-MM-YYYY"];
    const parsedDate = moment(data.target.value, spFormats, true);
    if (parsedDate.isValid()) {
      setValueDate(parsedDate);
    }
  };

  const checkDateDisabled = (current, validate, form) => {
    if (!validate || !validate.target) return false;
    const targetValue = form.getFieldValue(validate.target);
    if (!targetValue) return false;
    const spFormats = ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "DD-MM-YYYY"];
    const targetDate = moment(targetValue, spFormats, true);
    if (!targetDate.isValid()) return false;

    if (validate.operator === ">")
      return current && !current.isAfter(targetDate, "day");

    if (validate.operator === ">=")
      return current && current.isBefore(targetDate, "day");
    if (validate.operator === "<")
      return current && !current.isBefore(targetDate, "day");

    if (validate.operator === "<=")
      return current && current.isAfter(targetDate, "day");
    return false;
  };

  const handleRangeDate = (date, dateString) => {
    save(dateString, dataIndex);
    setOpenRangeDate(false);
    setValueRangeDate(date);
    form.setFieldsValue({
      [dataIndex]: dateString.join(" - "),
    });
  };

  const checkRangeDateUpdate = (data) => {
    const spFormats = ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "DD-MM-YYYY"];
    const dates = data.target.value.split(/(?<=\d{2}[\/\-]\d{4}|\s)-|to/);
    if (dates.length === 2) {
      const parsedStart = moment(dates[0].trim(), spFormats, true);
      const parsedEnd = moment(dates[1].trim(), spFormats, true);
      if (parsedStart.isValid() && parsedEnd.isValid()) {
        setValueRangeDate([parsedStart, parsedEnd]);
      }
    }
  };

  let childNode = children;
  const checkHandling = () => {
    if (isCreate !== true) {
      if (handling === true) {
        return false;
      }
      return true;
    }
    return false;
  };
  if (editable) {
    childNode =
      1 === 1 ? (
        <>
          <Form.Item
            style={{
              margin: 0,
            }}
            name={dataIndex}
            hasFeedback
            rules={[
              {
                required: required,
                message: `Vui lòng Nhập ${title}.`,
              },
              ...(type === "date-text" || type === "range-date-text"
                ? [
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const spFormats = [
                        "YYYY-MM-DD",
                        "DD/MM/YYYY",
                        "MM/DD/YYYY",
                        "DD-MM-YYYY",
                      ];
                      if (type === "date-text") {
                        const currentDate = moment(value, spFormats, true);
                        if (!currentDate.isValid()) {
                          return Promise.reject(
                            "Định dạng ngày không hợp lệ.",
                          );
                        }
                        if (checkDateDisabled(currentDate, validate, form)) {
                          return Promise.reject(
                            `Ngày phải thỏa mãn điều kiện ${validate.operator} với ${validate.target}`,
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]
                : []),
            ]}
            validateTrigger={["onChange", "onBlur"]}
            initialValue={record[dataIndex]}
          >
            {option ? (
              <Select
                disabled={checkHandling()}
                defaultValue={record[dataIndex]}
                onSelect={(e) => save(e, dataIndex)}
                onChange={(e) => save(e, dataIndex)}
                onKeyDown={handleKeyDown}
                onInputKeyDown={handleKeyDown}
                onPaste={handlePaste}
              >
                {option.map((item) => (
                  <Select.Option key={item} value={item}>
                    {item}
                  </Select.Option>
                ))}
              </Select>
            ) : type == "number" && dataIndex.includes("Số tiền") ? (
              <InputNumber
                readOnly={!!checkHandling()}
                ref={inputRef}
                min={0}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                style={{ width: "100%" }}
                onPressEnter={(e) => save(e, dataIndex)}
                onBlur={(e) => save(e, dataIndex)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onKeyPress={(e) => {
                  const charCode = e.which ? e.which : e.keyCode;
                  if (charCode < 48 || charCode > 57) {
                    e.preventDefault();
                  }
                }}
              />
            ) : type == "date-text" ? (
              <Input
                readOnly={!!checkHandling()}
                type={type}
                ref={inputRef}
                onPressEnter={(e) => {
                  save(e, dataIndex);
                  setOpenDate(false);
                }}
                onBlur={(e) => {
                  save(e, dataIndex);
                  setOpenDate(false);
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                autoComplete={"off"}
                min={0}
                onChange={(e) => checkDateUpdate(e)}
                className="edittable-input-date"
                placeholder={"DD/MM/YYYY |..."}
                addonAfter={
                  <>
                    <Button
                      disabled={!!checkHandling()}
                      icon={<CalendarOutlined />}
                      onClick={() => {
                        setOpenDate(!openDate);
                      }}
                    />
                    {openDate && (
                      <DatePicker
                        open={openDate}
                        onChange={handleDateChange}
                        format="DD/MM/YYYY"
                        onBlur={() => setOpenDate(false)}
                        onOpenChange={(isOpen) => setOpenDate(isOpen)}
                        className="edittable-date"
                        placement="topRight"
                        defaultValue={valueDate}
                        disabledDate={(current) =>
                          checkDateDisabled(current, validate, form)
                        }
                      />
                    )}
                  </>
                }
              />
            ) : type == "range-date-text" ? (
              <Input
                readOnly={!!checkHandling()}
                type={type}
                ref={inputRef}
                onPressEnter={(e) => {
                  save(e, dataIndex);
                  setOpenRangeDate(false);
                }}
                onBlur={(e) => {
                  save(e, dataIndex);
                  setOpenRangeDate(false);
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                autoComplete={"off"}
                min={0}
                onChange={(e) => checkRangeDateUpdate(e)}
                className="edittable-input-date"
                placeholder={"DD/MM/YYYY - DD/MM/YYYY |..."}
                addonAfter={
                  <>
                    <Button
                      disabled={!!checkHandling()}
                      icon={<CalendarOutlined />}
                      onClick={() => {
                        setOpenRangeDate(!openRangeDate);
                      }}
                    />
                    {openRangeDate && (
                      <RangePicker
                        ranges={{
                          "Hôm nay": [moment(), moment()],
                          "Tháng này": [
                            moment().startOf("month"),
                            moment().endOf("month"),
                          ],
                          "Năm nay": [
                            moment().startOf("year"),
                            moment().endOf("year"),
                          ],
                          "Năm ngoái": [
                            moment().add(-1, "year").startOf("year"),
                            moment().add(-1, "year").endOf("year"),
                          ],
                        }}
                        open={openRangeDate}
                        onBlur={() => setOpenRangeDate(false)}
                        format="DD/MM/YYYY"
                        className="edittable-date"
                        defaultValue={valueRangeDate}
                        onOpenChange={(isOpen) => setOpenRangeDate(isOpen)}
                        onChange={handleRangeDate}
                        placement="topRight"
                      />
                    )}
                  </>
                }
              />
            ) : (
              <Input
                readOnly={!!checkHandling()}
                type={type}
                ref={inputRef}
                onPressEnter={(e) => save(e, dataIndex)}
                onBlur={(e) => save(e, dataIndex)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                autoComplete={"off"}
                min={0}
              />
            )}
          </Form.Item>
        </>
      ) : (
        <div
          className="editable-cell-value-wrap"
          style={{
            paddingRight: 24,
          }}
          onClick={toggleEdit}
        >
          {children}
        </div>
      );
  }

  return <td {...restProps}>{childNode}</td>;
};

class EditableTable extends React.Component {
  constructor(props) {
    super(props);
    this.rowForms = {};
    if (this.props.isEditing && this.props.isCreate) {
      this.columns = [...props.columns];
    } else {
      this.columns = [...props.columns];
    }
    this.state = {
      dataSource: this.props.dataSource,
      columns: this.columns,
      count: this.props.dataSource.length + 1,
      isCreate: this.props.isCreate,
      isDetail: this.props.isDetail,
      is_roleUser: this.props.is_roleUser,
      is_username_create: this.props.is_username_create,
      fileList: [],
    };

    this.state.columns.map((col) => {
      if (col.hasOwnProperty("children")) {
        col.children.map((item) => {
          if (!item.title.includes("(*)") && item.required) {
            item.title = item.title + " (*)";
          }
        });
      }
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (JSON.stringify(this.props) !== JSON.stringify(prevProps)) {
      let columns = [];
      if (this.props.isEditing && this.props.isCreate) {
        columns = [...this.props.columns];
      } else {
        columns = [...this.props.columns];
      }
      this.setState({
        dataSource: this.props.dataSource,
        columns: columns,
        count: this.props.dataSource.length,
        isCreate: this.props.isCreate,
        isDetail: this.props.isDetail,
        is_roleUser: this.props.is_roleUser,
      });
    }
  }

  handleDelete = (key) => {
    const dataSource = [...this.state.dataSource];
    this.setState({
      dataSource: dataSource.filter((item) => item.key !== key),
    });
    this.props.callBackfunction(dataSource.filter((item) => item.key !== key));
  };

  handleAdd = () => {
    const { count, dataSource, columns } = this.state;
    const objCol = columns[0].children;
    let newData = {
      key: count,
    };
    for (let i = 0; i < objCol.length; i++) {
      newData[objCol[i].dataIndex] = "";
    }
    this.setState({
      dataSource: [...dataSource, newData],
      count: count + 1,
    });
  };

  handleSave = (row, newValue, dataIndex) => {
    row[dataIndex] = newValue;
    let newData = [...this.state.dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    this.setState({
      dataSource: newData,
    });
    this.props.callBackfunction(newData);
  };

  handlePasteList = (clipboardText, dataIndex, currentRecord) => {
    if (!clipboardText) return;

    // Lấy toàn bộ clipboard text (không split dòng) làm giá trị áp dụng
    const value = clipboardText.trim();
    if (!value) return;

    const { dataSource } = this.state;
    const updatedDataSource = [...dataSource];

    // Tìm vị trí row đang focus
    const focusedIndex = updatedDataSource.findIndex(
      (item) => String(item.key) === String(currentRecord.key),
    );
    if (focusedIndex === -1) return;

    // Bắt đầu từ record KẾ TIẾP sau record đang focus
    const startIndex = focusedIndex + 1;
    if (startIndex >= updatedDataSource.length) return;

    // Áp dụng giá trị cho toàn bộ các record phía dưới
    for (let i = startIndex; i < updatedDataSource.length; i++) {
      const row = { ...updatedDataSource[i] };

      let formattedValue = value;
      if (dataIndex.includes("Số tiền")) {
        formattedValue = value
          .replace(/,/g, "")
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }
      row[dataIndex] = formattedValue;
      updatedDataSource[i] = row;

      const rowForm = this.rowForms[row.key] || this.rowForms[String(row.key)];
      if (rowForm) {
        rowForm.setFieldsValue({
          [dataIndex]: formattedValue,
        });
      }
    }

    this.setState({ dataSource: updatedDataSource }, () => {
      this.props.callBackfunction(updatedDataSource);
    });
  };

  getTotalWidth = (cols) => {
    return cols.reduce((sum, col) => {
      if (col.children) {
        return sum + this.getTotalWidth(col.children);
      }
      return sum + (col.width || 0);
    }, 0);
  };

  render() {
    const { dataSource, isCreate, isDetail, is_roleUser, is_username_create } =
      this.state;
    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    };

    const mapColumns = (col) => {
      if (col.hasOwnProperty("ellipsis")) {
        if (col.ellipsis) {
          col.ellipsis = false;
        }
      }
      if (col.title == "Xóa") {
        return col;
      }

      const newCol = {
        ...col,

        onCell: (record) => ({
          record,
          editable:
            isCreate ||
            (is_roleUser
              ? is_username_create && col.editable
                ? true
                : !col.editable
              : col.editable),
          dataIndex: col.dataIndex,
          title: col.title,
          required: col.required,
          type: col.type,
          handling: col.handling,
          isCreate: isCreate,
          option: col.option,
          validate: col.validate,
          handleSave: this.handleSave,
        }),
      };

      if (col.children) {
        let dictColSTT = {
          title: "STT",
          dataIndex: "index",
          key: "index",
          fixed: "left",
          width: 70,
          render: (value, item, index) => index + 1,
          sortDirections: ["descend", "ascend", "descend"],
        };
        let colAction = {
          title: "Xóa",
          dataIndex: "operation",
          fixed: "right",
          width: 80,
          render: (text, record) =>
            this.state.dataSource.length > 1 ? (
              <Popconfirm
                title="Chắc chắn xóa ?"
                onConfirm={() => this.handleDelete(record.key)}
              >
                <a style={{ color: "#ff0000" }}>Xóa</a>
              </Popconfirm>
            ) : null,
        };
        newCol.children = col.children.map(mapColumns);
        !isCreate && newCol.children.unshift(dictColSTT);
        !isDetail &&
          this.state.dataSource.length > 1 &&
          newCol.children.push(colAction);
      }

      return newCol;
    };

    let filterColumns = this.state.columns;
    if (isCreate) {
      filterColumns = this.state.columns.filter(
        (item) => item.editable !== false || item.title == "Action",
      );
    }
    const columns = filterColumns.map(mapColumns);
    const totalWidth = this.getTotalWidth(columns);

    return (
      <TableContext.Provider
        value={{
          registerForm: (key, form) => {
            this.rowForms[key] = form;
          },
          unregisterForm: (key) => {
            delete this.rowForms[key];
          },
          handlePaste: this.handlePasteList,
          dataSource: this.state.dataSource,
        }}
      >
        {this.props.isEditing && this.props.isCreate ? (
          <>
            <Button
              onClick={this.handleAdd}
              type="primary"
              style={{ float: "right" }}
            >
              Thêm
            </Button>
          </>
        ) : null}

        {isDetail &&
          this.state.dataSource.length > 0 &&
          columns.map((item, index) => {
            const isEditing =
              this.props.isEditing || this.props.editingIndex === index;
            return (
              <Table
                components={isEditing ? components : null}
                bordered={true}
                dataSource={this.state.dataSource}
                columns={item.children}
                pagination={false}
                key={"detail_" + index}
                scroll={{ x: "max-content", y: 250 }}
                size="small"
                title={() =>
                  this.props.renderSectionTitle ? (
                    this.props.renderSectionTitle(item, index)
                  ) : (
                    <div style={{ color: item.colortitle }}>{item.title}</div>
                  )
                }
              />
            );
          })}

        {!isDetail && this.state.dataSource.length > 0 && (
          <Collapse
            defaultActiveKey={["0"]}
            bordered={false}
            className="customCollapse"
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            {columns.map((item, index) => {
              return (
                <Panel
                  header={
                    <span style={{ color: item.colortitle }}>{item.title}</span>
                  }
                  key={index}
                  style={{ color: "green" }}
                >
                  <Table
                    components={this.props.isEditing ? components : null}
                    bordered={true}
                    dataSource={this.state.dataSource}
                    columns={item.children}
                    pagination={false}
                    key={index}
                    scroll={{ x: totalWidth }}
                  />
                </Panel>
              );
            })}
          </Collapse>
        )}
      </TableContext.Provider>
    );
  }
}

export default EditableTable;
